/**
 * Kênh realtime với server (đường `/ws/app`, JSON thuần). TK-18: access token đi bằng subprotocol
 * `kapp-jwt, <accessToken>` — KHÔNG còn trong URL (NỢ-AT-05).
 *
 * Nguyên tắc:
 *  - Luôn tự nối lại khi rớt, giãn dần 1s → 2s → 4s … tối đa 20s.
 *  - Đăng ký `subscribe.all` để nhận sự kiện của MỌI tủ; việc lọc do phía web làm.
 *    (Nếu gửi `subscribe` kèm danh sách tủ, server sẽ chặn sự kiện của tủ khác,
 *     làm cây tuyến đường ở thanh bên đứng hình.)
 *  - Sự kiện chỉ dùng để LÀM MỚI dữ liệu (invalidate query), không tự vẽ trạng thái
 *    theo trí nhớ — nguồn sự thật vẫn là REST.
 */

import { buildWsUrl, forceRefreshToken, wsProtocols } from './api';
import type { SwitchPos } from './types';

/* ------------------------------------------------------- kiểu bản tin */

export interface RtOutputState {
  index: number;
  isOn: boolean;
  enabled: boolean;
  /*
   * ⚠ HAI TRƯỜNG TUỲ CHỌN, và "tuỳ chọn" ở đây là SỰ THẬT chứ không phải phòng xa:
   *   · đường **v1** (`onState`) không mang cả hai — tủ v1 không có khái niệm ấy;
   *   · đường **v2** mang chúng, nhưng `nguonGiuQuyen` chỉ có ở tủ đã nạp `NV108`.
   * ⛔ Vắng ⇒ để nguyên VẮNG. `?? 0` ở đây là biến *"tủ không nói"* thành *"tủ nói không ai
   *   giữ"* — đúng bệnh `0.000 A` (NV5), chỉ đổi chỗ đứng.
   *
   * 🔴 KHÁC `switchPos` ngay dưới: `switchPos` có nguồn REST nên khung này chỉ là tiếng vọng.
   *   `nguonGiuQuyen` **KHÔNG CÓ** nguồn REST — CSDL không có cột ấy — nên khung này là
   *   đường DUY NHẤT nó tới được trình duyệt. Vứt khung đi là làm nhãn câm.
   */
  manualHold?: boolean;
  nguonGiuQuyen?: number;
}

export type RtMessage =
  | { type: 'hello'; userId: string; role: string; at: number }
  | { type: 'pong'; at: number }
  /*
   * ⚠ `switchPos` là TUỲ CHỌN, và điều đó có thật chứ không phải phòng xa: máy chủ KHÔNG
   *   gửi trường này khi tủ báo một mã ngoài dải enum `SwitchPos` — giữ nguyên sự thật cũ
   *   còn hơn phát đi một chỗ trống trông như một giá trị.
   *
   * ⛔ Bản trước khai `switchPos: 'OFF' | 'MANUAL' | 'AUTO'` — một kiểu KHẲNG ĐỊNH rằng
   *   "chưa báo" không thể xảy ra, đúng cái điều cấm cốt lõi của dự án, ở đúng đường mang
   *   trạng thái sống. Hôm nay web chỉ dùng khung này để làm mới truy vấn (`queries.ts`) nên
   *   chưa ai đọc trường ấy — nhưng một kiểu nói sai thì không chờ có người đọc mới thành
   *   sai. Sự thật về `switchPos` đến từ REST (`SwitchPos | null`), không từ đây.
   */
  /*
   * ⚠ `khungNoiVeNguonGiu` — khung này CÓ ý kiến về `nguonGiuQuyen` hay không. Chỉ đường v2
   *   (`onKappState`, có ngõ) đặt `true`; đường v1 không bao giờ. Bên nhận KHÔNG được suy từ
   *   sự vắng mặt của trường: sau NV108, "cả ba ngõ không ai giữ" cho ra khung y hệt khung v1.
   */
  | { type: 'cabinet.state'; cabinetId: string; deviceId?: string; switchPos?: SwitchPos;
      outputs?: RtOutputState[]; khungNoiVeNguonGiu?: boolean; at: number }
  /* TK-16 B4: `lyDo` — firmware mới khai vì sao gửi (định kỳ 30 s / sự kiện); firmware cũ không khai ⇒ null. */
  | { type: 'cabinet.meter'; cabinetId: string; meter: Record<string, number>; at: number; lyDo?: 'DINH_KY' | 'SU_KIEN' | null }
  | { type: 'cabinet.online'; cabinetId: string; deviceId: string }
  | { type: 'cabinet.offline'; cabinetId: string }
  /*
   * ⚠ 04/09/2026 (YC-29 PHẦN 2) — nay do MỘT cửa `moCanhBao()` phát, và nó MANG `mucDo`.
   *   Trước đây ba chỗ tự phát, cả ba quên `mucDo`, mà quy tắc 1 của popup đọc đúng trường
   *   ấy. `mucDo: null` = không có mức riêng ⇒ tra `BANG_MUC` theo mã, KHÔNG phải mức 2.
   */
  | { type: 'cabinet.alarm'; id?: string; cabinetId: string; code?: string; message?: string;
      mucDo?: 1 | 2 | null; viec?: 'moi' | 'nang-muc'; luc?: number }
  /*
   * ⚠ NGUYÊN NHÂN ĐÃ HẾT — tủ nói, không phải người trực xác nhận (YC-47). Loại tin này CÓ
   *   từ lâu và **chưa từng có người nghe**: chỉ có khai báo kiểu ở đây, không handler nào.
   *   Một kênh phát vào chỗ không ai nghe trông y hệt một kênh chưa dựng — và đó chính là lý
   *   do băng `SAFETY_CUT` nằm lại sau khi tủ đã báo `safetyCut = 0`.
   * ⛔ Nay CHỈ `dongCanhBao()` phát nó, mỗi mã một dòng, và chỉ khi có hàng thật sự đóng.
   */
  | { type: 'cabinet.alarm.cleared'; cabinetId: string; codes?: string[]; message?: string;
      soHang?: number; luc?: number }
  | { type: 'cabinet.schedule'; cabinetId: string; schedule: unknown }
  | { type: 'cabinet.log'; cabinetId: string; message: string }
  /*
   * ⚠ `commandId` CÓ THỂ `null`: ACK của `SetDeviceTime` không mang mã lệnh (chốt YC-16
   *   vòng 2) — nó ghép bằng `requestId`. Chính tôi thêm nhánh ấy ở máy chủ mà quên nới kiểu
   *   ở đây; kiểu vẫn khai `string` trong khi bên phát gửi `null`.
   */
  | { type: 'command.ack'; commandId: string | null; requestId?: number; result: number; ten?: string; detail?: string | null; khongCoHang?: boolean }
  | { type: 'command.timeout'; commandId: string }
  | { type: string; [k: string]: unknown };

export type RtStatus = 'idle' | 'connecting' | 'open' | 'closed';

type MessageHandler = (msg: RtMessage) => void;
type StatusHandler = (s: RtStatus) => void;

/* ------------------------------------------------------------- client */

class RealtimeClient {
  private socket: WebSocket | null = null;
  private handlers = new Set<MessageHandler>();
  private statusHandlers = new Set<StatusHandler>();
  private status: RtStatus = 'idle';
  private retry = 0;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private wanted = false;

  /** Bắt đầu giữ kết nối (gọi sau khi đăng nhập xong). */
  start() {
    this.wanted = true;
    this.open();
  }

  /** Dừng hẳn (khi đăng xuất) — không nối lại nữa. */
  stop() {
    this.wanted = false;
    this.clearTimers();
    if (this.socket) {
      const s = this.socket;
      this.socket = null;
      try { s.close(1000, 'client stop'); } catch { /* noop */ }
    }
    this.setStatus('idle');
  }

  /** Đóng và mở lại ngay (dùng khi access token vừa được đổi mới). */
  refreshConnection() {
    if (!this.wanted) return;
    if (this.socket) {
      const s = this.socket;
      this.socket = null;
      try { s.close(4000, 'token refreshed'); } catch { /* noop */ }
    }
    this.retry = 0;
    this.open();
  }

  onMessage(h: MessageHandler): () => void {
    this.handlers.add(h);
    return () => this.handlers.delete(h);
  }

  onStatus(h: StatusHandler): () => void {
    this.statusHandlers.add(h);
    return () => this.statusHandlers.delete(h);
  }

  getStatus(): RtStatus {
    return this.status;
  }

  /* ------------------------------------------------------- nội bộ */

  private setStatus(s: RtStatus) {
    if (this.status === s) return;
    this.status = s;
    this.statusHandlers.forEach((h) => h(s));
  }

  private clearTimers() {
    if (this.reconnectTimer !== null) { window.clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    if (this.pingTimer !== null) { window.clearInterval(this.pingTimer); this.pingTimer = null; }
  }

  private open() {
    if (!this.wanted) return;
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) return;

    const url = buildWsUrl();
    const giaoThuc = wsProtocols();
    if (!url || !giaoThuc) {
      // Chưa có token — thử lại sau, đừng quay vòng liên tục.
      this.scheduleReconnect(3000);
      return;
    }

    this.setStatus('connecting');
    let socket: WebSocket;
    try {
      socket = new WebSocket(url, giaoThuc);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      if (this.socket !== socket) return;
      this.retry = 0;
      this.setStatus('open');
      this.send({ type: 'subscribe.all' });
      this.pingTimer = window.setInterval(() => this.send({ type: 'ping' }), 25_000);
    };

    socket.onmessage = (ev) => {
      if (this.socket !== socket) return;
      let msg: RtMessage;
      try { msg = JSON.parse(String(ev.data)) as RtMessage; } catch { return; }
      this.handlers.forEach((h) => {
        try { h(msg); } catch { /* một handler lỗi không được làm chết các handler khác */ }
      });
    };

    socket.onerror = () => { /* onclose sẽ chạy ngay sau, xử lý nối lại ở đó */ };

    socket.onclose = (ev: CloseEvent) => {
      if (this.socket !== socket) return;
      this.socket = null;
      if (this.pingTimer !== null) { window.clearInterval(this.pingTimer); this.pingTimer = null; }
      this.setStatus('closed');

      // 4401 = server từ chối token (xem index.ts). Đổi token rồi mới nối lại,
      // nếu không sẽ quay vòng vô ích với đúng token đã hết hạn.
      if (ev.code === 4401) {
        void forceRefreshToken().then((fresh) => {
          this.scheduleReconnect(fresh ? 500 : 15_000);
        });
        return;
      }
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(fixedDelay?: number) {
    if (!this.wanted || this.reconnectTimer !== null) return;
    const delay = fixedDelay ?? Math.min(1000 * 2 ** this.retry, 20_000);
    this.retry = Math.min(this.retry + 1, 5);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  private send(obj: unknown) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      try { this.socket.send(JSON.stringify(obj)); } catch { /* noop */ }
    }
  }
}

export const realtime = new RealtimeClient();
